import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Download, Mail, Newspaper, Tv } from "lucide-react";

export const Route = createFileRoute("/prensa")({
  head: () => ({
    meta: [
      { title: "Prensa — Bizaclia" },
      {
        name: "description",
        content:
          "Recursos para medios, kit de prensa y contacto del departamento de comunicación de Bizaclia.",
      },
      { property: "og:title", content: "Prensa — Bizaclia" },
      {
        property: "og:description",
        content:
          "Recursos para medios, kit de prensa y contacto de comunicación.",
      },
      { property: "og:url", content: "/prensa" },
    ],
    links: [{ rel: "canonical", href: "/prensa" }],
  }),
  component: PrensaPage,
});

function PrensaPage() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al inicio
      </Link>

      <header className="mb-10">
        <h1 className="text-4xl font-bold tracking-tight">Prensa</h1>
        <p className="mt-2 text-muted-foreground">
          Recursos para medios, comunicados y contacto con el departamento de comunicación de Bizaclia.
        </p>
      </header>

      {/* Press kit download */}
      <section className="mb-12 rounded-xl border border-border bg-background p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">Kit de prensa</h2>
            <p className="mt-1 text-muted-foreground text-sm">
              Logotipos, capturas de pantalla y notas de prensa oficiales para descargar.
            </p>
          </div>
          <button
            disabled
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground opacity-60 cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            Descargar ZIP
          </button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          El kit estará disponible próximamente. Mientras tanto, puedes solicitar materiales directamente a comunicacion@bizaclia.com
        </p>
      </section>

      {/* Stats / highlights */}
      <section className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-background p-5 text-center">
          <Newspaper className="mx-auto h-6 w-6 text-primary" />
          <p className="mt-2 text-2xl font-bold">50+</p>
          <p className="text-sm text-muted-foreground">Menciones en medios</p>
        </div>
        <div className="rounded-lg border border-border bg-background p-5 text-center">
          <Tv className="mx-auto h-6 w-6 text-primary" />
          <p className="mt-2 text-2xl font-bold">8</p>
          <p className="text-sm text-muted-foreground">Entrevistas</p>
        </div>
      </section>

      {/* Recent mentions */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-4">Menciones recientes</h2>
        <ul className="space-y-3">
          {[
            {
              media: "Expansión",
              title: "Bizaclia, el portal que revoluciona el traspaso de locales comerciales",
              date: "Marzo 2026",
            },
            {
              media: "El Confidencial",
              title: "Cómo la digitalización está cambiando el mercado inmobiliario comercial",
              date: "Enero 2026",
            },
            {
              media: "Cadena SER",
              title: "Entrevista: el auge de los traspasos post-pandemia",
              date: "Octubre 2025",
            },
          ].map((m) => (
            <li
              key={m.title}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 rounded-lg border border-border bg-background p-4"
            >
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-primary">
                  {m.media}
                </span>
                <p className="text-sm font-medium">{m.title}</p>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{m.date}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Contact */}
      <section className="rounded-xl border border-border bg-background p-6 md:p-8">
        <h2 className="text-xl font-semibold">Contacto de prensa</h2>
        <p className="mt-2 text-muted-foreground text-sm max-w-xl">
          Para entrevistas, solicitudes de información o colaboraciones con medios, escríbenos directamente.
        </p>
        <div className="mt-4 flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-primary" />
          <a href="mailto:comunicacion@bizaclia.com" className="font-medium hover:underline">
            comunicacion@bizaclia.com
          </a>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/mybizaclia"
            search={{ tab: "soporte" }}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Solicitar información
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Ver blog
          </Link>
        </div>
      </section>
    </main>
  );
}
